import React from 'react';
import { Button } from '@/components/ui/button';

interface InlineErrorBoundaryProps {
  children: React.ReactNode;
  title?: string;
}

interface InlineErrorBoundaryState {
  hasError: boolean;
  message: string;
}

class InlineErrorBoundary extends React.Component<InlineErrorBoundaryProps, InlineErrorBoundaryState & { retryCount: number }> {
  state = {
    hasError: false,
    message: '',
    retryCount: 0,
  };
  private retryTimer?: ReturnType<typeof setTimeout>;

  static getDerivedStateFromError(error: Error) {
    return {
      hasError: true,
      message: error.message || 'Unexpected section error',
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[InlineErrorBoundary] Section crash:', error, errorInfo);
    // Auto-retry once for transient errors (null ref, HMR)
    if (this.state.retryCount < 1) {
      this.retryTimer = setTimeout(() => {
        this.setState(prev => ({ hasError: false, message: '', retryCount: prev.retryCount + 1 }));
      }, 600);
    }
  }

  componentWillUnmount() {
    if (this.retryTimer) clearTimeout(this.retryTimer);
  }

  handleReset = () => {
    this.setState({ hasError: false, message: '', retryCount: 0 });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="rounded-2xl border border-border bg-card p-4 shadow-sm space-y-3">
        <div>
          <h2 className="text-sm font-semibold text-foreground">{this.props.title || 'Section crashed'}</h2>
          <p className="text-xs text-muted-foreground">A section failed to render, but the page is still available.</p>
        </div>
        {this.state.message ? (
          <p className="text-xs text-muted-foreground break-words rounded-md border border-border bg-muted/50 p-3">
            {this.state.message}
          </p>
        ) : null}
        <Button type="button" size="sm" variant="outline" onClick={this.handleReset}>
          Retry section
        </Button>
      </div>
    );
  }
}

export default InlineErrorBoundary;